import { useCallback } from 'react';
import {
  UseStatePersistenceReturn,
  StatePersistenceConfig,
  TableState,
} from '../../types';
import { useDebouncedCallback } from '../utils/use-debounce';

/**
 * Hook for persisting table state to localStorage
 *
 * This hook provides functionality to save and restore table state
 * (pagination, sorting, filters, etc.) to/from localStorage with
 * automatic debouncing to prevent excessive writes.
 *
 * @template T - The type of data rows in the table
 * @param config - Configuration object containing:
 *   - key: Storage key for localStorage
 *   - fields: Array of state fields to persist
 *   - debounceMs: Debounce delay for save operations
 *
 * @returns Object containing:
 *   - saveState: Function to save state to localStorage
 *   - loadState: Function to load state from localStorage
 *   - clearState: Function to clear saved state
 *
 * @example
 * ```tsx
 * const { saveState, loadState, clearState } = useStatePersistence({
 *   key: 'user-table-state',
 *   fields: ['pagination', 'sorting', 'filters'],
 *   debounceMs: 500
 * });
 *
 * // Save current state
 * saveState({
 *   pagination: { page: 2, rowsPerPage: 25 },
 *   sorting: { orderBy: 'name', order: 'asc' }
 * });
 *
 * // Load saved state
 * const savedState = loadState();
 *
 * // Clear saved state
 * clearState();
 * ```
 */
export const useStatePersistence = <T extends Record<string, unknown>>(
  config: StatePersistenceConfig,
): UseStatePersistenceReturn<T> => {
  const saveState = useCallback(
    (state: Partial<TableState<T>>) => {
      try {
        const stateToSave: Partial<TableState<T>> = {};

        // Only save the fields specified in config
        config.fields.forEach((field) => {
          if (state[field] !== undefined) {
            // Type assertion to handle the union types properly
            (stateToSave as Record<string, unknown>)[field] = (
              state as Record<string, unknown>
            )[field];
          }
        });

        const serializedState = JSON.stringify(stateToSave);

        switch (config.storage) {
          case 'localStorage': {
            localStorage.setItem(config.key, serializedState);
            break;
          }
          case 'sessionStorage': {
            sessionStorage.setItem(config.key, serializedState);
            break;
          }
          case 'url': {
            // For URL persistence, we'll use URLSearchParams
            const url = new URL(window.location.href);
            url.searchParams.set(config.key, serializedState);
            window.history.replaceState({}, '', url.toString());
            break;
          }
        }
      } catch (error) {
        console.warn('Failed to save table state:', error);
      }
    },
    [config],
  );

  const loadState = useCallback((): Partial<TableState<T>> | null => {
    try {
      let serializedState: string | null = null;

      switch (config.storage) {
        case 'localStorage': {
          serializedState = localStorage.getItem(config.key);
          break;
        }
        case 'sessionStorage': {
          serializedState = sessionStorage.getItem(config.key);
          break;
        }
        case 'url': {
          const url = new URL(window.location.href);
          serializedState = url.searchParams.get(config.key);
          break;
        }
      }

      if (serializedState) {
        return JSON.parse(serializedState) as Partial<TableState<T>>;
      }
    } catch (error) {
      console.warn('Failed to load table state:', error);
    }
    return null;
  }, [config]);

  const clearState = useCallback(() => {
    try {
      switch (config.storage) {
        case 'localStorage': {
          localStorage.removeItem(config.key);
          break;
        }
        case 'sessionStorage': {
          sessionStorage.removeItem(config.key);
          break;
        }
        case 'url': {
          const url = new URL(window.location.href);
          url.searchParams.delete(config.key);
          window.history.replaceState({}, '', url.toString());
          break;
        }
      }
    } catch (error) {
      console.warn('Failed to clear table state:', error);
    }
  }, [config]);

  // Debounced save function to prevent excessive saves
  const debouncedSaveState = useDebouncedCallback(
    (state: unknown) => saveState(state as Partial<TableState<T>>),
    config.debounceMs || 500,
  );

  return {
    saveState: (state: Partial<TableState<T>>) => debouncedSaveState(state),
    loadState,
    clearState,
  };
};
